import { useContext, useEffect, useRef, useState } from 'react'
import { useLocation, Navigate } from 'react-router-dom'
import { SmileOutlined } from '@ant-design/icons'
import { Result } from 'antd'
import { AuthContext } from '../Firebase/firebaseContext'
import { FirestoreContext } from '../Firebase/firestoreContext'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { NEXT3PL_TENANT_PROFILE } from '../../constants/routes'
import Breadcrumb from '../Breadcrumb'
import { CheckIcon } from '@heroicons/react/solid'
import * as queryString from 'query-string'
import { GOOGLE_CLOUD_PROJECT_ID } from '../../constants/constants'
import { doc, getDoc, updateDoc } from '@firebase/firestore'
import PropTypes from 'prop-types'
import { validateOauthCallback } from './oauthCallback'

const ConnectorDashboard = props => {
  const { hidden } = props
  const location = useLocation()
  const { user } = useContext(AuthContext)
  const { firestore, userDoc } = useContext(FirestoreContext)
  const [errorMessage, setErrorMessage] = useState('')
  // Guards against the exchange running twice (React StrictMode in development,
  // and the effect re-firing as firestore/user settle).
  const exchangeStarted = useRef(false)
  const [steps] = useState([
    { id: '01', name: 'Choose platform', href: '#', status: 'complete' },
    { id: '02', name: 'Enter Details', href: '#', status: 'complete' },
    { id: '03', name: 'Complete', href: '#', status: 'complete' }
  ])

  useEffect(() => {
    if (!firestore || !user || exchangeStarted.current) {
      return
    }
    exchangeStarted.current = true

    const doAsyncStuff = async () => {
      const params = queryString.parse(location.search)

      const ebayOauthDoc = await getDoc(doc(firestore, 'ebay_oauth', user.uid))

      // Rejects a mismatched 'state', i.e. an authorisation code we did not ask
      // for. See oauthCallback.js.
      const result = validateOauthCallback(
        params,
        ebayOauthDoc.exists() ? ebayOauthDoc.data() : null,
        'eBay'
      )
      if (!result.valid) {
        setErrorMessage(result.message)
        return
      }

      const data = {
        ...result.stored,
        authorizationCode: { ...params, createdAt: new Date() }
      }
      await updateDoc(doc(firestore, 'ebay_oauth', user.uid), {
        data,
        updatedAt: new Date()
      })

      // This step needs to be done at the server side, not client side.
      // The token exchange is done on the user's behalf, so the request has to
      // prove who is asking. The server must verify this token and confirm the
      // state below belongs to that same user.
      const idToken = await user.getIdToken()
      await window
        .fetch(
          `https://australia-southeast1-${GOOGLE_CLOUD_PROJECT_ID}.cloudfunctions.net/ebay-oauth-cloud-function`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + idToken
            },
            body: JSON.stringify({ state: params.state })
          }
        )
        .then(res => res.json()) // expecting a json response
    }

    doAsyncStuff().catch(error => {
      console.error(error)
      setErrorMessage('Something went wrong while completing the connection. Please try again.')
    })
  }, [firestore, user, location.search])

  return (
    <>
      {userDoc && !userDoc.isNextLogisticsCustomer
        ? (
          <>
            <Breadcrumb
              hidden={hidden}
              pages={[{ name: 'Connector Setup', href: '#', current: true }]}
            />
            <div className='pb-12 sm:rounded-lg shadow'>
              <nav aria-label='Progress'>
                <ol className='border border-gray-300 divide-y divide-gray-300 md:flex md:divide-y-0'>
                  {steps.map((step, stepIdx) => (
                    <li key={step.name} className='relative md:flex-1 md:flex'>
                      {step.status === 'complete'
                        ? (
                          <div className='group flex items-center w-full'>
                            <span className='px-6 py-4 flex items-center text-sm font-medium'>
                              <span className='flex-shrink-0 w-10 h-10 flex items-center justify-center bg-indigo-600 rounded-full group-hover:bg-indigo-800'>
                                <CheckIcon
                                  className='w-6 h-6 text-white'
                                  aria-hidden='true'
                                />
                              </span>
                              <span className='ml-4 text-sm font-medium text-gray-900'>
                                {step.name}
                              </span>
                            </span>
                          </div>
                          )
                        : step.status === 'current'
                          ? (
                            <div
                              className='px-6 py-4 flex items-center text-sm font-medium'
                              aria-current='step'
                            >
                              <span className='flex-shrink-0 w-10 h-10 flex items-center justify-center border-2 border-indigo-600 rounded-full'>
                                <span className='text-indigo-600'>{step.id}</span>
                              </span>
                              <span className='ml-4 text-sm font-medium text-indigo-600'>
                                {step.name}
                              </span>
                            </div>
                            )
                          : (
                            <div className='group flex items-center'>
                              <span className='px-6 py-4 flex items-center text-sm font-medium'>
                                <span className='flex-shrink-0 w-10 h-10 flex items-center justify-center border-2 border-gray-300 rounded-full group-hover:border-gray-400'>
                                  <span className='text-gray-500 group-hover:text-gray-900'>
                                    {step.id}
                                  </span>
                                </span>
                                <span className='ml-4 text-sm font-medium text-gray-500 group-hover:text-gray-900'>
                                  {step.name}
                                </span>
                              </span>
                            </div>
                            )}

                      {stepIdx !== steps.length - 1
                        ? (
                          <>
                            {/* Arrow separator for lg screens and up */}
                            <div
                              className='hidden md:block absolute top-0 right-0 h-full w-5'
                              aria-hidden='true'
                            >
                              <svg
                                className='h-full w-full text-gray-300'
                                viewBox='0 0 22 80'
                                fill='none'
                                preserveAspectRatio='none'
                              >
                                <path
                                  d='M0 -2L20 40L0 82'
                                  vectorEffect='non-scaling-stroke'
                                  stroke='currentcolor'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            </div>
                          </>
                          )
                        : null}
                    </li>
                  ))}
                </ol>
              </nav>
              <div className='pt-4'>
                {errorMessage
                  ? (
                    <Result
                      status='error'
                      title='We could not complete the eBay connection'
                      subTitle={errorMessage}
                    />
                    )
                  : (
                    <Result
                      icon={<SmileOutlined />}
                      title='Great, we have done all the operations!'
                      subTitle='It may take a few minutes for your changes to be processed'
                    />
                    )}
              </div>
            </div>
          </>
          )
        : (
          <>{userDoc && <Navigate to={ NEXT3PL_TENANT_PROFILE } />}</>
          )}
    </>
  )
}

ConnectorDashboard.propTypes = {
  hidden: PropTypes.bool.isRequired
}

export default ConnectorDashboard
